// Copyright (c) Meta Platforms, Inc. and affiliates.

/**
 * @file RadioIndicator.tsx
 * @input Indicator state props
 * @output Exports RadioIndicator — the default radio selection visual
 * @position Decorative radio visual shared by RadioList, decorative menu
 *           markers, and any selection slot themed to use a radio
 */

import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { borderVars, colorVars, durationVars, easeVars, radiusVars } from "../theme/tokens.stylex.js";
import { isRenderable, mergeProps, themeProps } from "../utils/index.js";
import "./indicator.markers.stylex.js";
import { indicatorScope } from "./indicator.markers.stylex.js";
import { jsx as _jsx } from "react/jsx-runtime";
const styles = {
  circle: {
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    kMzoRj: "x1litavf",
    ksu8eU: "x1y0btm7",
    kaIpWk: "xjspbzw",
    k1ekBW: "xts7igz",
    kIyJzY: "xuedmi6 x12w9bfk",
    kAMwcw: "xlr8y92",
    $$css: true
  },
  unchecked: {
    kVAM5u: "xvy26l8 xilc7fp",
    kWkggS: "x10xzikg xeultfy",
    $$css: true
  },
  checked: {
    kVAM5u: "xad5do x1ozl1p1",
    kWkggS: "x1ewilqj x17srpfb",
    $$css: true
  },
  disabled: {
    kSiTet: "xbyyjgo",
    kVAM5u: "x14i3s5s x61roa7",
    $$css: true
  },
  disabledUnchecked: {
    kWkggS: "xwmxj5m xqxnq6d",
    $$css: true
  },
  dot: {
    kaIpWk: "xjspbzw",
    kWkggS: "x1azo05 xwvh9j7",
    $$css: true
  }
};
const circleSizeStyles = {
  sm: {
    kzqmXN: "xw4jnvo",
    kZKoxP: "x1qx5ct2",
    $$css: true
  },
  md: {
    kzqmXN: "xvy4d1p",
    kZKoxP: "xxk0z11",
    $$css: true
  }
};
const dotSizeStyles = {
  sm: {
    kzqmXN: "x1xc55vz",
    kZKoxP: "xdk7pt",
    $$css: true
  },
  md: {
    kzqmXN: "x1fsd2vl",
    kZKoxP: "x170jfvy",
    $$css: true
  }
};

/**
 * The default radio visual: a circle with a filled inner dot when selected.
 *
 * Decorative and non-interactive — it renders `aria-hidden` and owns no input,
 * role, or focus behavior. Themes replace it wholesale through
 * `defineTheme({indicators: {radio: MyRadio}})`, or restyle it through the
 * `radio` / `radio-dot` theme targets like any other component.
 *
 * Unlike an icon, a radio draws in *both* states — an empty circle when
 * unchecked. That is what makes it usable as a selection indicator in
 * components whose default is "a checkmark when selected, nothing otherwise".
 *
 * @example
 * ```
 * <RadioIndicator state="checked" size="md" />
 * ```
 */
export function RadioIndicator({
  state,
  size = 'md',
  isDisabled = false,
  children,
  ref,
  className,
  style,
  xstyle,
  ...rest
}) {
  // A radio has no partial state; anything other than unchecked reads as
  // selected.
  const isChecked = state !== 'unchecked';
  return /*#__PURE__*/_jsx("span", {
    ...rest,
    ref: ref,
    "aria-hidden": "true",
    ...mergeProps(themeProps('radio-indicator', {
      size,
      checked: isChecked ? 'checked' : null,
      disabled: isDisabled ? 'disabled' : null
    },
    // `radio` was the target before indicators existed; themes styling
    // it keep working until the next major.
    {
      legacyNames: ['radio']
    }), stylex.props(styles.circle, circleSizeStyles[size], isChecked ? styles.checked : styles.unchecked, isDisabled && styles.disabled, isDisabled && !isChecked && styles.disabledUnchecked, xstyle), className, style),
    children: isRenderable(children) ? children : isChecked && /*#__PURE__*/_jsx("span", {
      ...mergeProps(themeProps('radio-indicator-dot', {
        size
      }, {
        legacyNames: ['radio-dot']
      }), stylex.props(styles.dot, dotSizeStyles[size]))
    })
  });
}
RadioIndicator.displayName = 'RadioIndicator';